/**
 * What an operator has told the app to do for *them*, as opposed to what the room is set up to
 * do for everybody in it.
 *
 * Stored in localStorage for the same reason the tournament list's default filter is
 * (`tournamentFilter.ts`): it is a view preference rather than data, and the API has no
 * user-preferences surface — adding one would mean an entity, a migration and endpoints for
 * something that only decides which room the app opens on. The trade-off is the same, and it is
 * stated on the screen rather than left to be discovered: it does not follow an operator between
 * machines.
 *
 * Not keyed by room, unlike the filter — this preference *is* the choice of room.
 */

const DEFAULT_ROOM_KEY = 'brush.room.default';

/** The room the app should open on, or null if the operator has never chosen one. */
export function loadDefaultRoomId(): string | null {
  try {
    const raw = window.localStorage.getItem(DEFAULT_ROOM_KEY);
    return raw !== null && raw.length > 0 ? raw : null;
  } catch {
    // Private browsing or a disabled store. Falling back to the first room is correct; a
    // preference is never worth failing a screen over.
    return null;
  }
}

export function saveDefaultRoomId(id: string): void {
  try {
    window.localStorage.setItem(DEFAULT_ROOM_KEY, id);
  } catch {
    /* see loadDefaultRoomId */
  }
}

export function clearDefaultRoomId(): void {
  try {
    window.localStorage.removeItem(DEFAULT_ROOM_KEY);
  } catch {
    /* see loadDefaultRoomId */
  }
}

/**
 * Whether the Rundown rail is hidden to its handle (#575): a view preference of the same kind, per browser and not per
 * room. Shown by default, and stored only while hidden, so showing it again leaves nothing behind. The store is reached
 * for inside the `try`, since reaching for `window.localStorage` is itself what throws when site data is blocked.
 */
const RUNDOWN_HIDDEN_KEY = 'brush.room.rundownHidden';

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
const local = (): Store => window.localStorage;

export function loadRundownHidden(store: () => Store = local): boolean {
  try {
    return store().getItem(RUNDOWN_HIDDEN_KEY) === 'true';
  } catch {
    /* see loadDefaultRoomId: shown is the safe answer */
    return false;
  }
}

export function saveRundownHidden(hidden: boolean, store: () => Store = local): void {
  try {
    if (hidden) store().setItem(RUNDOWN_HIDDEN_KEY, 'true');
    else store().removeItem(RUNDOWN_HIDDEN_KEY);
  } catch {
    /* see loadDefaultRoomId */
  }
}
